import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import {
  getAreasApi,
  getClientsApi,
  getEmployeeDetailsApi,
  getEmployeesApi,
} from './api/client-customer-map.api';
import type { IEmployeeCustomerMap } from './types';

import EmployeeDetails from './components/EmployeeDetails';
import EmployeeList from './components/EmployeeList';

export default function ClientCustomerMap() {
  const [selectedEmployee, setSelectedEmployee] =
    useState<IEmployeeCustomerMap | null>(null);
  const queryClient = useQueryClient();

  // Avatar server paths - memoized to prevent re-creation
  const avatarPaths = useMemo(
    () => ({
      thumb: `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/thumb/`,
      full: `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/full/`,
    }),
    [],
  );

  // Fetch employees data
  const {
    data: employeesData,
    isLoading: employeesLoading,
    refetch: refetchEmployees,
  } = useQuery({
    queryKey: ['employee-customer-map-employees'],
    queryFn: getEmployeesApi,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch areas data
  const { data: areasData } = useQuery({
    queryKey: ['employee-customer-map-areas'],
    queryFn: getAreasApi,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch clients data
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['employee-customer-map-clients'],
    queryFn: getClientsApi,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch detailed employee data
  const {
    data: employeeDetailsData,
    isLoading: employeeDetailsLoading,
    error: employeeDetailsError,
    refetch: refetchEmployeeDetails,
  } = useQuery({
    queryKey: ['employee-customer-map-details', selectedEmployee?._id],
    queryFn: () => getEmployeeDetailsApi(selectedEmployee!._id),
    enabled: !!selectedEmployee,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Memoized data extraction to prevent unnecessary re-calculations
  const processedData = useMemo(
    () => ({
      employees: employeesData?.data || [],
      areas: areasData?.data || [],
      clients: clientsData?.data || [],
      employeeDetails: employeeDetailsData?.data || null,
    }),
    [employeesData, areasData, clientsData, employeeDetailsData],
  );

  // Optimized employee selection handler
  const handleEmployeeSelect = useCallback(
    (employee: IEmployeeCustomerMap) => {
      // Only clear cache if selecting a different employee
      if (selectedEmployee && selectedEmployee._id !== employee._id) {
        queryClient.removeQueries({
          queryKey: ['employee-customer-map-details', selectedEmployee._id],
        });
      }
      setSelectedEmployee(employee);
    },
    [selectedEmployee, queryClient],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <Helmet>
        <title>
          توزيع العملاء على الموظفين | Healthy Empire | إدارة الموارد البشرية
        </title>
        <meta
          name="description"
          content="إدارة توزيع العملاء والمناطق على الموظفين - نظام Healthy Empire المتقدم لإدارة الموارد البشرية"
        />
        <meta
          name="keywords"
          content="توزيع العملاء, إدارة الموارد البشرية, Healthy Empire, توزيع المناطق, صلاحيات الموظفين"
        />
      </Helmet>
      {/* Animated Background Elements */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-40 -top-40 h-80 w-80 animate-pulse rounded-full bg-gradient-to-br from-blue-400/20 to-purple-600/20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 h-80 w-80 animate-pulse rounded-full bg-gradient-to-tr from-indigo-400/20 to-pink-600/20 blur-3xl delay-1000"></div>
        <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 transform animate-pulse rounded-full bg-gradient-to-r from-cyan-400/10 to-blue-600/10 blur-3xl delay-500"></div>
      </div>

      <div className="relative space-y-8 p-6">
        {/* Main Grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Employee List */}
          <div className="space-y-6 lg:col-span-1">
            <EmployeeList
              employees={processedData.employees}
              isLoading={employeesLoading}
              selectedEmployee={selectedEmployee}
              onEmployeeSelect={handleEmployeeSelect}
              avatarServerPath={avatarPaths.thumb}
              onRefresh={refetchEmployees}
            />
          </div>

          {/* Right Column - Employee Details */}
          <div className="lg:col-span-2">
            <EmployeeDetails
              selectedEmployee={selectedEmployee}
              employeeDetails={processedData.employeeDetails}
              isLoading={employeeDetailsLoading}
              error={employeeDetailsError}
              avatarFullPath={avatarPaths.full}
              avatarServerPath={avatarPaths.thumb}
              employees={processedData.employees}
              areas={processedData.areas}
              clients={processedData.clients}
              clientsLoading={clientsLoading}
              onRetry={refetchEmployeeDetails}
            />
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(148, 163, 184, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #7c3aed);
        }
      `}</style>
    </div>
  );
}
